import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, ChevronDown, X, Menu, Copy } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import clsx from 'clsx';

export default function Header() {
  const { exchanges } = useAuth();
  const { toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);

  const connectedExchanges = exchanges?.filter(e => e.is_connected) || [];
  const hasConnection = connectedExchanges.length > 0;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/discover?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  return (
    <header className="sticky top-0 z-40 h-16 bg-bg/80 backdrop-blur-sm border-b border-border">
      <div className="h-full px-4 md:px-6 flex items-center justify-between gap-2 md:gap-4">
        {/* Mobile: Hamburger + Logo */}
        <div className="flex items-center gap-3 md:hidden">
          <button
            onClick={toggleSidebar}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-tertiary rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent-primary flex items-center justify-center">
              <Copy className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-text-primary">MIMIQ</span>
          </div>
        </div>

        {/* Search - hidden on mobile, visible on md+ */}
        <form onSubmit={handleSearch} className="hidden md:block flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search traders, instruments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-bg-tertiary border border-border rounded-lg text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>

        {/* Right Section */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* Mobile Search Button */}
          <button
            onClick={() => navigate('/discover')}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-tertiary rounded-lg transition-colors md:hidden"
            aria-label="Search"
          >
            <Search className="w-5 h-5" />
          </button>


          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-text-secondary hover:text-text-primary hover:bg-bg-tertiary rounded-lg transition-colors"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-loss rounded-full" />
            </button>

            {showNotifications && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowNotifications(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-72 md:w-80 bg-bg-secondary border border-border rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="p-4 border-b border-border">
                    <h3 className="font-medium text-text-primary">Notifications</h3>
                  </div>
                  <div className="p-4 text-center text-text-muted text-sm">
                    No new notifications
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Connection Status - simplified on mobile */}
          <div
            className={clsx(
              'flex items-center gap-2 px-2 md:px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium',
              hasConnection
                ? 'bg-profit/10 text-profit border border-profit/20'
                : 'bg-bg-tertiary text-text-muted border border-border'
            )}
          >
            <div
              className={clsx(
                'w-2 h-2 rounded-full',
                hasConnection ? 'bg-profit animate-pulse' : 'bg-text-muted'
              )}
            />
            <span className="hidden sm:inline">{hasConnection ? 'Connected' : 'No Exchange'}</span>
            {hasConnection && connectedExchanges.length > 1 && (
              <ChevronDown className="w-3 h-3 hidden sm:block" />
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
